// Considered: reusing AspectAttachmentSheetMolecule's shell — rejected: that sheet edits an
// attachment already on the type; this dialog starts a brand-new aspect definition before one
// exists, with its own routes/duplicate-detection concern the attachment sheet has no reason to
// carry. Considered: DialogWrapper / CustomDialogAtom — rejected for the same reason
// `SampleRecordDialogMolecule` rejected it: PrimeReact's Dialog portals to `document.body`.
//
// Source: aspect-start.jsx:4-108 (`NewAspectDialog`) — opened from the composer's "Draft a new
// aspect" add-row button (`trail-composer.jsx:1367`, `compose.draft-a-new-aspect`). The duplicate
// -detection list (`hits`) is real: it searches the caller's aspect catalog by word overlap
// against the name/intent text, same as the prototype's `SIMILAR` filter — but the prototype's
// per-hit "{{types}} types · {{records}} records" line names marketplace usage stats this
// platform has no endpoint for yet, so this renders the one usage figure `AspectRow` actually
// carries (its own property count) rather than inventing the other two.
//
// `railMode` (EAINT-933 Phase 3.2, D-EAINT-933 "rail for every add and edit"): the composer now
// mounts this as the rail's `start` selection — no portal, no own scrim/`role="dialog"`/
// `aria-modal`, no own focus trap (`InspectorRailOrganism` already traps/Escapes non-modally).
// The contract's own `compose > dialog > dialog` shape stays intact as the RELATIVE container
// path below the rail's `side > start` mount, same convention `AspectAttachmentSheetMolecule`
// uses for its `side > attach` mount.

import React from 'react';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useTranslation } from 'localization';
import type { AspectRow } from 'types/aspect';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './NewAspectDialogMolecule.scss';

const KEY = 'studio.typeEditor.aspects.newDialog';

export type NewAspectRoute = 'describe' | 'extend' | 'blank';

export interface NewAspectDraft {
  route: NewAspectRoute;
  name: string;
  intent: string;
  /** The `extend` route's parent — a `system_name` picked from `aspectCatalog` (`compose.extend`),
   * never free text: a parent that is not in the catalog is not a parent. */
  extendsAspect?: string;
}

export interface NewAspectDialogMoleculeProps {
  visible: boolean;
  /**
   * Renders as the rail's current selection body (no scrim, no `role="dialog"`/`aria-modal`, no
   * own focus trap — the host, `InspectorRailOrganism`, already owns Escape and containment
   * non-modally). Default `false` keeps the standalone-modal shape for any host that has not
   * moved to the rail.
   */
  railMode?: boolean;
  /** The composing type's display name (`compose.for`'s `{forType}`). */
  forTypeName?: string | null;
  /** The workspace's aspect catalog, searched for near-duplicates. */
  aspectCatalog?: AspectRow[] | null;
  onClose: () => void;
  onCreate: (draft: NewAspectDraft) => void;
  /** `[Use this]` on a duplicate hit — attaches the existing aspect instead of drafting one. */
  onUseExisting?: (systemName: string) => void;
  /** `[Extend it]` on a duplicate hit — starts a draft with that aspect as parent. The button is
   * rendered only when this is supplied: a host with no extend flow gets no dead affordance. */
  onExtendExisting?: (systemName: string) => void;
  /**
   * Fires whenever "has the author typed something worth losing" changes (EAINT-933 Phase 3.3:
   * "dirty draft never lost silently"). A host that closes the rail selection without checking
   * this discards `name`/`intent`/`extendsAspect` with no warning — the same silent loss the
   * standalone-modal shape never risked, since the modal's own scrim click was the only path in
   * (and still lost it) but rail mode adds three more (rail head close, Escape, switching
   * selections). The molecule reports dirtiness rather than the host guessing at its fields,
   * since only the molecule knows which route's fields count.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * `onCreate`'s most recent failure (EAINT-933 Phase 3.4 `draftAspectCollisionKeepsTheDraft`)
   * — a system_name collision or a create failure, already translated by the host. The dialog
   * stays open and `name`/`intent`/`extendsAspect` stay exactly as typed: a submit that closed
   * on failure would discard the author's words for an error they never saw fixed.
   */
  error?: string | null;
  /** True for the duration of an in-flight `onCreate` — the draft button disables so a slow
   * create can't be double-submitted, same guard `handleKeepGhost` uses for Keep. */
  submitting?: boolean;
}

interface RouteOption {
  id: NewAspectRoute;
  icon: string;
}

const ROUTES: RouteOption[] = [
  { id: 'describe', icon: 'pi pi-sparkles' },
  { id: 'extend', icon: 'pi pi-sitemap' },
  { id: 'blank', icon: 'pi pi-file' },
];

const WORD_MIN_LENGTH = 2;

function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > WORD_MIN_LENGTH);
}

const NewAspectDialogMolecule: React.FC<NewAspectDialogMoleculeProps> = ({
  visible,
  railMode = false,
  forTypeName = null,
  aspectCatalog = null,
  onClose,
  onCreate,
  onUseExisting,
  onExtendExisting,
  onDirtyChange,
  error = null,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  const draftReasonId = React.useId();
  const [route, setRoute] = React.useState<NewAspectRoute>('describe');
  const [name, setName] = React.useState('');
  const [intent, setIntent] = React.useState('');
  const [extendsAspect, setExtendsAspect] = React.useState('');

  // Reset on close: a dialog that reopens holding the last attempt's words would start the
  // next draft from something the user didn't say (aspect-start.jsx:10).
  React.useEffect(() => {
    if (!visible) {
      setRoute('describe');
      setName('');
      setIntent('');
      setExtendsAspect('');
    }
  }, [visible]);

  // "Worth losing" is whichever route's own fields hold text — the route toggle itself never
  // counts (its default, `describe`, is not a change the author made).
  const isDirty = name.trim() !== '' || intent.trim() !== '' || extendsAspect !== '';
  React.useEffect(() => {
    onDirtyChange?.(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  const dialogRef = React.useRef<HTMLDivElement>(null);
  // Rail mode: an expanded selection inside the rail, not a modal — the rail's own
  // `useDialogFocus` (`trapFocus: false`) already manages focus and Escape at its container,
  // same as `AspectAttachmentSheetMolecule`'s `side > attach` mount.
  useDialogFocus(visible, dialogRef, railMode ? { onDismiss: onClose, trapFocus: false, manageFocus: false } : { onDismiss: onClose });

  const queryWords = React.useMemo(() => wordsOf(name || intent), [name, intent]);
  const hits = React.useMemo(() => {
    if (queryWords.length === 0 || !aspectCatalog?.length) return [];
    return aspectCatalog.filter((row) => {
      const haystack = `${getLocalizedText(row.name, row.system_name)} ${getLocalizedText(row.description ?? undefined, '')}`.toLowerCase();
      return queryWords.some((word) => haystack.includes(word));
    });
  }, [aspectCatalog, queryWords]);

  if (!visible) return null;

  const draftLabel = route === 'describe' ? t(`${KEY}.draftIt`) : t(`${KEY}.createDraft`);
  // Each route's own inputs gate its submit: an extension with no parent named is not a draft.
  // The reason the primary is disabled is stated beside it (ux-craft I2) — on the extend route two
  // fields gate it, and a bare disabled button says nothing about which one is missing.
  const draftMissing: 'intent' | 'name' | 'parent' | null =
    route === 'describe' ? (intent.trim() ? null : 'intent') : !name.trim() ? 'name' : route === 'extend' && !extendsAspect ? 'parent' : null;
  const draftDisabled = draftMissing !== null;
  const parentOptions = (aspectCatalog ?? []).map((row) => ({
    label: getLocalizedText(row.name, row.system_name),
    value: row.system_name,
  }));

  // Rail mode: the composer's `side > start` mount already supplies the surface segment, so
  // this component's own nested containers stay relative to it — the same convention
  // `AspectAttachmentSheetMolecule` uses below its `side > attach` mount. Standalone-modal mode
  // keeps the contract's literal `compose > dialog > dialog` path.
  const containerBase = railMode ? 'start' : 'compose > dialog > dialog';

  const content = (
    <div
      className="new-aspect-dialog"
      ref={dialogRef}
      data-datum="compose.dialog#2"
      data-kind="dialog"
      data-container={railMode ? undefined : 'compose > dialog'}
      data-testid="new-aspect-dialog"
      role={railMode ? undefined : 'dialog'}
      aria-modal={railMode ? undefined : 'true'}
      aria-labelledby={titleId}
      onClick={railMode ? undefined : (event) => event.stopPropagation()}
    >
      <div className="new-aspect-dialog__head" data-container={containerBase}>
        <strong id={titleId} data-datum="compose.new-aspect" data-kind="strong">
          {t(`${KEY}.title`)}
        </strong>
        {forTypeName && (
          <span className="new-aspect-dialog__crumb" data-datum="compose.for" data-kind="breadcrumb">
            {t(`${KEY}.forCrumb`, { forType: forTypeName })}
          </span>
        )}
        <ButtonAtom
          variant="text"
          size="small"
          label={t(`${KEY}.close`)}
          onClick={onClose}
          data-datum="compose.close#2"
          data-kind="button"
          data-testid="new-aspect-dialog-close"
        />
      </div>

      <div className="new-aspect-dialog__body" data-container={`${containerBase} > group`}>
        <div className="new-aspect-dialog__routes" data-container={`${containerBase} > group > group`}>
          {ROUTES.map((option) => (
            <button
              key={option.id}
              type="button"
              className="new-aspect-dialog__route"
              data-on={route === option.id ? '1' : undefined}
              aria-pressed={route === option.id}
              onClick={() => setRoute(option.id)}
              data-datum="compose.button#2"
              data-kind="button"
              data-container={`${containerBase} > group > group > each(ROUTES)`}
              data-testid={`new-aspect-dialog-route-${option.id}`}
            >
              <i className={option.icon} aria-hidden="true" />
              <strong data-datum="compose.r-label" data-kind="strong">
                {t(`${KEY}.routes.${option.id}.label`)}
              </strong>
              <span className="new-aspect-dialog__route-note">{t(`${KEY}.routes.${option.id}.note`)}</span>
            </button>
          ))}
        </div>

        {route === 'describe' && (
          <label
            className="new-aspect-dialog__field"
            data-datum="compose.what-do-you-want-to-measure"
            data-kind="textarea"
            data-layout="stacked"
            data-container={`${containerBase} > group`}
          >
            {t(`${KEY}.describeLabel`)}
            <TextareaAtom
              rows={3}
              value={intent}
              onChange={(event) => setIntent(event.target.value)}
              placeholder={t(`${KEY}.describePlaceholder`)}
              data-testid="new-aspect-dialog-intent"
            />
          </label>
        )}
        {route !== 'describe' && (
          <label
            className="new-aspect-dialog__field"
            data-datum="compose.name"
            data-kind="input"
            data-layout="stacked"
            data-container={`${containerBase} > group`}
          >
            {t(`${KEY}.nameLabel`)}
            <InputAtom
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t(`${KEY}.namePlaceholder`)}
              data-testid="new-aspect-dialog-name"
            />
          </label>
        )}
        {route === 'extend' && (
          <label
            className="new-aspect-dialog__field"
            data-datum="compose.extend"
            data-kind="select"
            data-layout="stacked"
            data-container={`${containerBase} > group`}
          >
            {t(`${KEY}.extendLabel`)}
            {/*
                The prototype sketches this as a search box (`aspect-start.jsx:57`, "Search aspects to
                inherit from…"); the choice is finite — the catalog this dialog already holds — so it
                is a filterable select over that catalog, not prose the host would have to resolve
                (ux-craft I5). `appendTo="self"` keeps the option panel inside the dialog's DOM.
              */}
            <DropdownAtom
              dropdownSize="small"
              value={extendsAspect || null}
              options={parentOptions}
              optionLabel="label"
              optionValue="value"
              filter
              filterBy="label,value"
              showClear
              appendTo="self"
              placeholder={t(`${KEY}.extendPlaceholder`)}
              emptyFilterMessage={t(`${KEY}.extendNoMatch`)}
              emptyMessage={t(`${KEY}.extendEmptyCatalog`)}
              onChange={(event) => setExtendsAspect((event.value as string | null) ?? '')}
              data-testid="new-aspect-dialog-extend"
            />
          </label>
        )}

        {/* Catching a duplicate here is worth more than anything else on this screen. */}
        {hits.length > 0 && (
          <div className="new-aspect-dialog__similar" data-container={`${containerBase} > group > group#2`}>
            <div className="new-aspect-dialog__similar-head" data-datum="compose.existing" data-kind="heading">
              <i className="pi pi-copy" aria-hidden="true" />
              <strong data-datum="compose.existing#2" data-kind="strong">
                {t(`${KEY}.existingCount`, { count: hits.length })}
              </strong>
              <span className="new-aspect-dialog__hint" data-datum="compose.using-one-of-these-keeps-everyone-measur" data-kind="hint">
                {t(`${KEY}.existingHint`)}
              </span>
            </div>
            {hits.map((hit) => (
              <div
                key={hit.system_name}
                className="new-aspect-dialog__similar-row"
                data-container={`${containerBase} > group > group#2 > each(hits) > list-item`}
              >
                <div className="new-aspect-dialog__similar-main">
                  <div className="new-aspect-dialog__similar-name">
                    <strong data-datum="compose.s-name" data-kind="strong">
                      {getLocalizedText(hit.name, hit.system_name)}
                    </strong>
                    {!hit.organization_id && (
                      <span className="new-aspect-dialog__pill" data-datum="compose.platform" data-kind="pill">
                        {t(`${KEY}.platformPill`)}
                      </span>
                    )}
                    <span className="new-aspect-dialog__hint" data-datum="compose.s-machinename" data-kind="hint">
                      {hit.system_name}
                    </span>
                  </div>
                  <span className="new-aspect-dialog__hint" data-datum="compose.types" data-kind="hint">
                    {t(`${KEY}.stats`, { count: hit.definition?.properties?.length ?? 0 })}
                  </span>
                </div>
                <div className="new-aspect-dialog__similar-acts">
                  {/* Primary on purpose (`aspect-start.jsx:84` `a-btn-p`): reusing the
                        existing aspect is the row's first action — "catching a duplicate
                        here is worth more than anything else on this screen". */}
                  {onUseExisting && (
                    <ButtonAtom
                      variant="primary"
                      size="small"
                      label={t(`${KEY}.useThis`)}
                      onClick={() => onUseExisting(hit.system_name)}
                      data-datum="compose.use-this"
                      data-kind="button"
                      data-testid={`new-aspect-dialog-use-${hit.system_name}`}
                    />
                  )}
                  {onExtendExisting && (
                    <ButtonAtom
                      variant="secondary"
                      size="small"
                      label={t(`${KEY}.extendIt`)}
                      onClick={() => onExtendExisting(hit.system_name)}
                      data-datum="compose.extend-it"
                      data-kind="button"
                      data-testid={`new-aspect-dialog-extend-${hit.system_name}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="new-aspect-dialog__foot" data-container={containerBase}>
        <span
          className="new-aspect-dialog__hint"
          data-datum="compose.same-three-routes-as-the-gallery-an-aspe"
          data-kind="hint"
          data-testid="new-aspect-dialog-foot-hint"
        >
          {forTypeName
            ? t(`${KEY}.sameRoutesHint`)
            : route === 'describe'
              ? t(`${KEY}.agentDraftsHint`)
              : t(`${KEY}.changeLaterHint`)}
        </span>
        <ButtonAtom
          variant="secondary"
          size="small"
          label={t(`${KEY}.cancel`)}
          onClick={onClose}
          data-datum="compose.cancel"
          data-kind="button"
          data-testid="new-aspect-dialog-cancel"
        />
        {draftMissing && (
          <span id={draftReasonId} className="new-aspect-dialog__draft-reason" data-testid="new-aspect-dialog-draft-reason">
            {t(`${KEY}.draftNeeds.${draftMissing}`)}
          </span>
        )}
        {error && (
          <span className="new-aspect-dialog__error" role="alert" data-testid="new-aspect-dialog-error">
            {error}
          </span>
        )}
        {/* One primary per surface (ux-craft I1): while near-duplicates are listed with a
              host that can adopt one, `Use this` on those rows is the primary — catching the
              duplicate is the point of the screen — and drafting anew steps back to secondary. */}
        <ButtonAtom
          variant={hits.length > 0 && onUseExisting ? 'secondary' : 'primary'}
          size="small"
          label={submitting ? t(`${KEY}.drafting`) : draftLabel}
          disabled={draftDisabled || submitting}
          aria-describedby={draftMissing ? draftReasonId : undefined}
          onClick={() =>
            onCreate({
              route,
              name: name.trim(),
              intent: intent.trim(),
              ...(route === 'extend' ? { extendsAspect } : {}),
            })
          }
          data-datum="compose.draft-it"
          data-kind="button"
          data-testid="new-aspect-dialog-draft"
        />
      </div>
    </div>
  );

  if (railMode) return content;

  return (
    /*
      The scrim closes on click, which is why it carries no keyboard role of its own: Close is
      the keyboard path, and a `role="button"` here would put an unlabelled control in the tab
      order in front of the dialog's own.
    */
    <div
      className="new-aspect-dialog__scrim"
      data-datum="compose.dialog"
      data-kind="dialog"
      data-container="compose"
      data-testid="new-aspect-dialog-scrim"
      role="presentation"
      onClick={onClose}
    >
      {content}
    </div>
  );
};

NewAspectDialogMolecule.displayName = 'NewAspectDialogMolecule';

export default NewAspectDialogMolecule;
